const CART_TOGGLE_SELECTORS = ['#cart-button', '.cart-toggle', '.header-cart-toggle', '[data-toggle="cart"]'];
const FOCUSABLE_SELECTORS = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
const STORE_PATHS = {
    CART: 'cart',
    ITEM: (key) => `cart/items/${encodeURIComponent(key)}`,
    COUPONS: 'cart/coupons'
};

export function initCart() {
    if (typeof window === 'undefined') return;
    window.PlancModules = window.PlancModules || {};
    if (window.PlancModules.cart) return;
    window.PlancModules.cart = true;

    const storeConfig = resolveStoreConfig();
    if (!storeConfig.root) {
        if (window.PLANC_DEBUG && console) console.warn('planc cart: missing Store API root.');
        return;
    }

    const els = mapElements();
    if (!els.sidebar || !els.overlay || !els.itemsContainer) {
        if (window.PLANC_DEBUG && console) console.warn('planc cart: sidebar markup not found.');
        return;
    }

    const state = {
        cart: null,
        isOpen: false,
        updatingKeys: new Set(),
        qtyTimers: new Map(),
        lastFocused: null,
        couponBusy: false
    };

    bindToggleButtons(openCart);
    bindStaticEvents();
    bindDynamicEvents();
    observeWooEvents();
    fetchCart();

    window.openCart = openCart;
    window.closeCart = closeCart;

    async function fetchCart(options = {}) {
        const { showLoader = false } = options;
        try {
            if (showLoader) setLoading(true);
            const data = await storeRequest(STORE_PATHS.CART);
            handleCartResponse(data);
        } catch (error) {
            notify(parseError(error) || 'Unable to load cart', 'error');
        } finally {
            if (showLoader) setLoading(false);
        }
    }

    function handleCartResponse(cart) {
        if (!cart) return;
        state.cart = cart;
        renderCart(cart);
        renderTotals(cart);
        updateBadges(cart.items_count || 0);
        updateShippingProgress(cart);
    }

    function bindToggleButtons(handler) {
        const buttons = new Set();
        CART_TOGGLE_SELECTORS.forEach((selector) => {
            document.querySelectorAll(selector).forEach((btn) => buttons.add(btn));
        });
        buttons.forEach((btn) => {
            btn.addEventListener('click', (event) => {
                event.preventDefault();
                handler({ refresh: true });
            });
        });
    }

    function bindStaticEvents() {
        els.overlay.addEventListener('click', () => closeCart());
        if (els.closeButton) els.closeButton.addEventListener('click', () => closeCart());
        if (els.continueShopping) els.continueShopping.addEventListener('click', () => closeCart());
        if (els.sidebar) els.sidebar.addEventListener('click', (event) => event.stopPropagation());
        document.addEventListener('keydown', handleKeydown);

        if (els.toggleCoupon) {
            els.toggleCoupon.addEventListener('click', () => {
                const form = els.couponForm;
                if (!form) return;
                form.classList.toggle('hidden');
                const icon = els.toggleCoupon.querySelector('svg');
                if (icon) icon.classList.toggle('rotate-180');
            });
        }

        if (els.applyCouponButton) {
            els.applyCouponButton.addEventListener('click', () => {
                const code = els.couponInput ? els.couponInput.value.trim() : '';
                if (!code) {
                    notify('Enter a coupon code', 'info');
                    return;
                }
                applyCoupon(code);
            });
        }
    }

    function bindDynamicEvents() {
        els.itemsContainer.addEventListener('click', (event) => {
            const incBtn = event.target.closest('.cart-qty-increase');
            if (incBtn) {
                let key = incBtn.getAttribute('data-cart-key');
                if (!key) {
                    const li = incBtn.closest('[data-cart-key]');
                    key = li ? li.getAttribute('data-cart-key') : null;
                }
                if (window.PLANC_DEBUG && console) console.debug('cart: increase click', { key, target: event.target });
                event.preventDefault();
                changeQuantity(key, 1);
                return;
            }

            const decBtn = event.target.closest('.cart-qty-decrease');
            if (decBtn) {
                let key = decBtn.getAttribute('data-cart-key');
                if (!key) {
                    const li = decBtn.closest('[data-cart-key]');
                    key = li ? li.getAttribute('data-cart-key') : null;
                }
                if (window.PLANC_DEBUG && console) console.debug('cart: decrease click', { key, target: event.target });
                event.preventDefault();
                changeQuantity(key, -1);
                return;
            }

            const removeBtn = event.target.closest('.cart-item-remove');
            if (removeBtn) {
                let key = removeBtn.getAttribute('data-cart-key');
                if (!key) {
                    const li = removeBtn.closest('[data-cart-key]');
                    key = li ? li.getAttribute('data-cart-key') : null;
                }
                if (window.PLANC_DEBUG && console) console.debug('cart: remove click', { key, target: event.target });
                event.preventDefault();
                removeItem(key);
            }
        });

        els.itemsContainer.addEventListener('change', (event) => {
            const input = event.target.closest('.cart-qty-input');
            if (!input) return;
            let key = input.getAttribute('data-cart-key');
            if (!key) {
                const li = input.closest('[data-cart-key]');
                key = li ? li.getAttribute('data-cart-key') : null;
            }
            if (window.PLANC_DEBUG && console) console.debug('cart: qty change', { key, value: input.value });
            const value = parseInt(input.value, 10);
            const normalized = Number.isFinite(value) && value > 0 ? value : 1;
            input.value = normalized;
            if (!key) return;
            if (state.qtyTimers.has(key)) clearTimeout(state.qtyTimers.get(key));
            const timer = setTimeout(() => {
                setQuantity(key, normalized);
                state.qtyTimers.delete(key);
            }, 250);
            state.qtyTimers.set(key, timer);
        });
    }

    function observeWooEvents() {
        try {
            const $ = window.jQuery || window.$;
            if ($ && $.fn) {
                $(document.body).on('added_to_cart', () => {
                    notify('Product added to cart', 'success');
                    openCart({ refresh: true });
                });
                $(document.body).on('removed_from_cart', () => fetchCart());
                return;
            }
        } catch (error) {
            if (window.PLANC_DEBUG && console) console.debug('planc cart: woo observers failed', error);
        }

        document.body.addEventListener('added_to_cart', () => openCart({ refresh: true }));
    }

    function openCart(options = {}) {
        const { refresh = false } = options;
        if (state.isOpen) {
            if (refresh) fetchCart({ showLoader: true });
            return;
        }

        state.isOpen = true;
        state.lastFocused = document.activeElement;
        els.sidebar.classList.remove('translate-x-full');
        els.sidebar.setAttribute('aria-hidden', 'false');
        showOverlay();
        document.body.classList.add('overflow-hidden');
        setBackgroundInert(true);
        trapFocus();

        if (refresh || !state.cart) fetchCart({ showLoader: true });
        requestAnimationFrame(() => animateItems());
    }

    function closeCart() {
        if (!state.isOpen) return;
        state.isOpen = false;
        els.sidebar.classList.add('translate-x-full');
        els.sidebar.setAttribute('aria-hidden', 'true');
        hideOverlay();
        document.body.classList.remove('overflow-hidden');
        setBackgroundInert(false);
        releaseFocusTrap();
        if (state.lastFocused && typeof state.lastFocused.focus === 'function') {
            try { state.lastFocused.focus(); } catch (e) { /* ignore */ }
        }
    }

    function showOverlay() {
        els.overlay.classList.remove('hidden');
        requestAnimationFrame(() => {
            els.overlay.classList.add('opacity-100');
        });
    }

    function hideOverlay() {
        els.overlay.classList.remove('opacity-100');
        setTimeout(() => {
            if (!state.isOpen) els.overlay.classList.add('hidden');
        }, 250);
    }

    function setBackgroundInert(enable) {
        const regions = [document.querySelector('main') || document.getElementById('content'), document.querySelector('header'), document.querySelector('footer')];
        regions.forEach((region) => {
            if (!region) return;
            if ('inert' in region) {
                try { region.inert = enable; } catch (e) { /* ignore */ }
            } else {
                if (enable) {
                    region.setAttribute('aria-hidden', 'true');
                } else {
                    region.removeAttribute('aria-hidden');
                }
            }
        });
    }

    function trapFocus() {
        document.addEventListener('focusin', enforceFocusTrap);
    }

    function releaseFocusTrap() {
        document.removeEventListener('focusin', enforceFocusTrap);
    }

    function enforceFocusTrap(event) {
        if (!state.isOpen || !els.sidebar) return;
        if (!els.sidebar.contains(event.target)) {
            const focusable = getFocusableElements();
            if (focusable.length) focusable[0].focus();
        }
    }

    function handleKeydown(event) {
        if (!state.isOpen) return;
        if (event.key === 'Escape') {
            event.preventDefault();
            closeCart();
            return;
        }

        if (event.key === 'Tab') {
            const focusable = getFocusableElements();
            if (!focusable.length) {
                event.preventDefault();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey) {
                if (document.activeElement === first || !els.sidebar.contains(document.activeElement)) {
                    event.preventDefault();
                    last.focus();
                }
            } else if (document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
    }

    function getFocusableElements() {
        return Array.from(els.sidebar.querySelectorAll(FOCUSABLE_SELECTORS)).filter((node) => node.offsetParent !== null);
    }

    function setLoading(show) {
        if (!els.loading) return;
        if (show) {
            els.loading.classList.remove('hidden');
            els.itemsContainer.classList.add('opacity-50');
        } else {
            els.loading.classList.add('hidden');
            els.itemsContainer.classList.remove('opacity-50');
        }
    }

    function renderCart(cart) {
        const template = els.itemTemplate;
        const emptyTemplate = els.emptyTemplate;
        els.itemsContainer.innerHTML = '';

        const items = Array.isArray(cart.items) ? cart.items : [];
        if (!template || !items.length) {
            if (emptyTemplate) {
                els.itemsContainer.appendChild(emptyTemplate.content.cloneNode(true));
                const startBtn = els.itemsContainer.querySelector('.start-shopping-btn');
                if (startBtn) {
                    startBtn.addEventListener('click', () => {
                        closeCart();
                        const shopUrl = window.planc?.cart?.shop_url;
                        if (shopUrl) window.location.href = shopUrl;
                    });
                }
                const icon = els.itemsContainer.querySelector('.empty-cart-animation svg');
                if (icon) icon.classList.add('animate-bounce');
            }
            return;
        }

        const list = document.createElement('ul');
        list.className = 'space-y-3';

        items.forEach((item, index) => {
            const li = template.content.firstElementChild.cloneNode(true);
            li.setAttribute('data-cart-key', item.key);
            li.classList.add('opacity-0', 'translate-x-4');

            const image = item.images?.[0];
            const imageEl = li.querySelector('.cart-item-image');
            if (imageEl) {
                imageEl.src = image?.thumbnail || image?.src || imageEl.src;
                imageEl.alt = image?.alt || item.name || '';
            }

            const nameAnchor = li.querySelector('.cart-item-name a');
            if (nameAnchor) {
                nameAnchor.textContent = item.name;
                nameAnchor.href = item.permalink || '#';
            }

            const priceEl = li.querySelector('.cart-item-total');
            if (priceEl) priceEl.innerHTML = formatPrice(item?.totals?.line_total ?? item?.prices?.price, cart.totals);

            const qtyInput = li.querySelector('.cart-qty-input');
            if (qtyInput) {
                qtyInput.value = item.quantity;
                qtyInput.setAttribute('data-cart-key', item.key);
            }

            // Add data-cart-key to interactive controls so event delegation can identify the item
            const controls = li.querySelectorAll('.cart-qty-input, .cart-qty-increase, .cart-qty-decrease, .cart-item-remove');
            controls.forEach((node) => node.setAttribute('data-cart-key', item.key));

            if (item.quantity <= 1) {
                const decBtn = li.querySelector('.cart-qty-decrease');
                if (decBtn) decBtn.setAttribute('disabled', 'disabled');
            }

            requestAnimationFrame(() => {
                setTimeout(() => li.classList.remove('opacity-0', 'translate-x-4'), index * 45);
            });

            list.appendChild(li);
        });

        els.itemsContainer.appendChild(list);
    }

    function renderTotals(cart) {
        const totals = cart.totals || {};
        if (els.total) els.total.innerHTML = formatPrice(totals.total_price, totals);
        if (els.subtotal) els.subtotal.innerHTML = formatPrice(totals.total_items, totals);
        if (els.itemCount) els.itemCount.textContent = cart.items_count || 0;

        const discountRaw = parseFloat(totals.total_discount || '0');
        if (!els.discountRow) return;
        if (discountRaw > 0) {
            els.discountRow.classList.remove('hidden');
            if (els.discountAmount) els.discountAmount.textContent = `-${formatPrice(discountRaw, totals)}`;
        } else {
            els.discountRow.classList.add('hidden');
        }
    }

    function updateShippingProgress(cart) {
        if (!els.shippingProgress) return;
        const threshold = Number(storeConfig.freeShipping || 0);
        const subtotal = parseFloat(cart?.totals?.total_items || '0');
        if (!threshold || !subtotal) {
            els.shippingProgress.classList.add('hidden');
            return;
        }

        els.shippingProgress.classList.remove('hidden');
        if (subtotal >= threshold) {
            if (els.shippingMessage) els.shippingMessage.textContent = '🎉 Great news! You qualify for free shipping!';
            if (els.shippingBar) els.shippingBar.style.width = '100%';
        } else {
            const remaining = Math.max(0, threshold - subtotal);
            const pct = Math.min(100, (subtotal / threshold) * 100);
            if (els.shippingMessage) els.shippingMessage.textContent = `Add ${formatPrice(remaining, cart.totals)} more for free shipping`;
            if (els.shippingBar) els.shippingBar.style.width = `${pct}%`;
        }
    }

    function updateBadges(count) {
        try {
            if (typeof window.plancUpdateCartBadge === 'function') {
                window.plancUpdateCartBadge(count);
                return;
            }
        } catch (error) {
            if (window.PLANC_DEBUG && console) console.debug('planc cart badge update failed', error);
        }

        const badges = document.querySelectorAll('#cart-badge, #mobile-cart-badge, .mobile-cart-badge');
        badges.forEach((badge) => {
            badge.textContent = count;
            if (count > 0) {
                badge.classList.remove('hidden');
            } else {
                badge.classList.add('hidden');
            }
        });
    }

    async function changeQuantity(cartKey, delta) {
        if (!cartKey || !state.cart) return;
        const current = state.cart.items?.find((item) => item.key === cartKey);
        const nextQty = Math.max(1, (current?.quantity || 1) + delta);
        await setQuantity(cartKey, nextQty);
    }

    async function setQuantity(cartKey, quantity) {
        if (!cartKey) return;
        setLineBusy(cartKey, true);
        try {
            const data = await storeRequest(STORE_PATHS.ITEM(cartKey), {
                method: 'PUT',
                body: { quantity }
            });
            handleCartResponse(data);
            notify('Cart updated', 'success');
        } catch (error) {
            notify(parseError(error) || 'Failed to update quantity', 'error');
            await fetchCart();
        } finally {
            setLineBusy(cartKey, false);
        }
    }

    async function removeItem(cartKey) {
        if (!cartKey) return;
        setLineBusy(cartKey, true);
        try {
            const data = await storeRequest(STORE_PATHS.ITEM(cartKey), { method: 'DELETE' });
            handleCartResponse(data);
            notify('Item removed', 'success');
        } catch (error) {
            notify(parseError(error) || 'Failed to remove item', 'error');
            await fetchCart();
        } finally {
            setLineBusy(cartKey, false);
        }
    }

    async function applyCoupon(code) {
        if (state.couponBusy) return;
        state.couponBusy = true;
        setCouponLoading(true);
        try {
            const data = await storeRequest(STORE_PATHS.COUPONS, {
                method: 'POST',
                body: { code }
            });
            handleCartResponse(data);
            notify('Coupon applied', 'success');
            if (els.couponInput) els.couponInput.value = '';
        } catch (error) {
            notify(parseError(error) || 'Unable to apply coupon', 'error');
        } finally {
            state.couponBusy = false;
            setCouponLoading(false);
        }
    }

    function setCouponLoading(loading) {
        if (!els.applyCouponButton) return;
        if (loading) {
            els.applyCouponButton.setAttribute('disabled', 'disabled');
            els.applyCouponButton.classList.add('opacity-50', 'pointer-events-none');
        } else {
            els.applyCouponButton.removeAttribute('disabled');
            els.applyCouponButton.classList.remove('opacity-50', 'pointer-events-none');
        }
    }

    function setLineBusy(cartKey, busy) {
        if (!cartKey) return;
        const line = els.itemsContainer.querySelector(`[data-cart-key="${cartKey}"]`);
        if (!line) return;
        if (busy) {
            state.updatingKeys.add(cartKey);
            line.classList.add('opacity-50', 'pointer-events-none');
        } else {
            state.updatingKeys.delete(cartKey);
            line.classList.remove('opacity-50', 'pointer-events-none');
        }
    }

    function animateItems() {
        const lines = els.itemsContainer.querySelectorAll('.cart-item');
        lines.forEach((line, index) => {
            setTimeout(() => line.classList.remove('opacity-0', 'translate-x-4'), index * 40);
        });
    }

    function notify(message, type = 'success') {
        try {
            if (window.PlancNotify && typeof window.PlancNotify.show === 'function') {
                window.PlancNotify.show(message, type);
                return;
            }
        } catch (error) {
            if (window.PLANC_DEBUG && console) console.debug('planc notify failed', error);
        }

        if (els.notification && els.notificationMessage) {
            els.notificationMessage.textContent = message;
            els.notification.classList.remove('translate-x-full');
            setTimeout(() => {
                els.notification.classList.add('translate-x-full');
            }, 3000);
            return;
        }

        if (window.PLANC_DEBUG && console) console.log(`[${type}] ${message}`);
    }

    async function storeRequest(path, options = {}) {
        const { method = 'GET', body } = options;
        const url = buildUrl(path);
        const headers = { Accept: 'application/json', ...(options.headers || {}) };
        if (body && !(body instanceof FormData)) headers['Content-Type'] = 'application/json';
        if (storeConfig.nonce) {
            headers['X-WC-Store-API-Nonce'] = storeConfig.nonce;
            // WooCommerce Store API expects the nonce in the `Nonce` header for update routes.
            headers['Nonce'] = storeConfig.nonce;
        }
        if (storeConfig.wpNonce) headers['X-WP-Nonce'] = storeConfig.wpNonce;

        const response = await fetch(url, {
            method,
            body: body && !(body instanceof FormData) ? JSON.stringify(body) : body,
            credentials: 'same-origin',
            headers
        });

        let data = null;
        try {
            data = await response.json();
        } catch (error) {
            // ignore empty body
        }

        if (!response.ok) {
            const error = new Error((data && (data.message || data.data?.message)) || 'Request failed');
            error.data = data;
            throw error;
        }

        return data;
    }

    function buildUrl(path) {
        const cleanRoot = storeConfig.root.replace(/\/$/, '');
        const cleanPath = (path || '').replace(/^\//, '');
        return `${cleanRoot}/${cleanPath}`;
    }

    function parseError(error) {
        if (!error) return null;
        if (typeof error === 'string') return error;
        if (error.data && error.data.message) return error.data.message;
        if (error.message) return error.message;
        return null;
    }

    function formatPrice(amount, totals = {}) {
        if (amount === undefined || amount === null) amount = 0;
        const symbol = totals.currency_symbol || storeConfig.currencySymbol || '$';
        const decimals = Number.isFinite(totals.currency_minor_unit) ? totals.currency_minor_unit : 2;
        const decimalSep = totals.currency_decimal_separator || '.';
        const thousandSep = totals.currency_thousand_separator || ',';
        const numeric = typeof amount === 'number' ? amount : parseFloat(amount || '0');
        const parts = numeric.toFixed(decimals).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
        const formatted = decimals ? parts.join(decimalSep) : parts[0];
        const prefix = totals.currency_prefix || symbol;
        const suffix = totals.currency_suffix || '';
        return `${prefix}${formatted}${suffix}`;
    }
}

function resolveStoreConfig() {
    const planc = window.planc || {};
    const storeApi = planc.storeApi || {};
    const cartCfg = planc.cart || {};
    const root = (storeApi.root || window.wcStoreApi?.endpoints?.[0]?.namespace || '').replace(/\/$/, '');
    return {
        root,
        nonce: storeApi.nonce || window.wcStoreApiNonce || null,
        wpNonce: storeApi.wpNonce || window.wpApiSettings?.nonce || null,
        freeShipping: cartCfg.free_shipping_threshold || storeApi.freeShippingThreshold || 0,
        currencySymbol: cartCfg.currency_symbol || cartCfg.currencySymbol || '$'
    };
}

function mapElements() {
    return {
        sidebar: document.getElementById('cart-sidebar'),
        overlay: document.getElementById('cart-overlay'),
        itemsContainer: document.getElementById('cart-items-container'),
        loading: document.getElementById('cart-loading'),
        total: document.getElementById('cart-total'),
        subtotal: document.getElementById('cart-subtotal'),
        itemCount: document.getElementById('cart-item-count'),
        discountRow: document.getElementById('cart-discounts'),
        discountAmount: document.getElementById('discount-amount'),
        shippingProgress: document.getElementById('shipping-progress'),
        shippingBar: document.getElementById('shipping-bar'),
        shippingMessage: document.getElementById('shipping-message'),
        closeButton: document.getElementById('close-cart'),
        continueShopping: document.getElementById('continue-shopping'),
        toggleCoupon: document.getElementById('toggle-coupon'),
        couponForm: document.getElementById('coupon-form'),
        couponInput: document.getElementById('coupon-code'),
        applyCouponButton: document.getElementById('apply-coupon'),
        notification: document.getElementById('cart-notification'),
        notificationMessage: document.getElementById('notification-message'),
        itemTemplate: document.getElementById('cart-item-template'),
        emptyTemplate: document.getElementById('empty-cart-template')
    };
}

export default initCart;
